import { get } from "lodash";
import { ReactElement, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import { Button } from "components/base/Button";
import { ReactComponent as CheckmarkIcon } from "../icons/Member/Checkmark.svg";
import { ReactComponent as LoadingIcon } from "../icons/Member/Loading.svg";
import Loading from "components/common/Loading";
import FlexPage from "components/layout/FlexPage";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Task, TaskWishlistEntry } from "protogen/tasks_pb";
import {
  useListTasksForMember,
  useListTaskProjects,
  useListMemberTaskWishlistEntries,
  useUpdateMemberTaskWishlistEntry,
} from "services/tasks";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import TaskInspiration from "components/tasks/TaskInspiration";
import MemberTaskSection from "components/tasks/MemberTaskSection";
import useTitle from "components/hooks/useTitle";
import SearchBar from "components/common/SearchBar";
import { defaultTaskSorter } from "components/tasks/TasksTable";
import { StageValue } from "components/tasks/workflowStates";
import { LIGHTNING_DETECTION_STAGE } from "types/delegation";
import CheckedDropDown from "components/common/CheckedDropDown";
import { ListTaskProjectsRequest } from "protogen/tasks_service_pb";
import { ReactComponent as EllipseIcon } from "../icons/Menu/Ellipse.svg";
import { Heart, Trash, X } from "lucide-react";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import FlexBox from "components/common/FlexBox";

const MEMBER_STAGE_FILTERS: {
  value: string;
  label: string;
  stages: string[];
  variant: "teal" | "red" | "gray" | "green" | "warning" | "outlined";
}[] = [
  {
    value: StageValue.WaitingOnClient,
    label: "Needs review",
    stages: [StageValue.WaitingOnClient],
    variant: "red",
  },
  {
    value: StageValue.New,
    label: "Submitted",
    stages: [StageValue.New, LIGHTNING_DETECTION_STAGE],
    variant: "gray",
  },
  {
    value: StageValue.InQueue,
    label: "In queue",
    stages: [StageValue.InQueue],
    variant: "outlined",
  },
  {
    value: StageValue.Active,
    label: "Active",
    stages: [StageValue.Active, StageValue.AdvisorReview],
    variant: "teal",
  },
  {
    value: StageValue.OnHold,
    label: "On hold",
    stages: [StageValue.OnHold],
    variant: "gray",
  },
];

const stageFilterOptions = MEMBER_STAGE_FILTERS.map((f) => ({
  label: f.label,
  value: f.value,
  mutuallyExclusive: false,
  labelElement: <Chip size="small" label={f.label} variant={f.variant} />,
}));

const getStagesForFilterValues = (values: string[]): string[] =>
  MEMBER_STAGE_FILTERS.filter((f) => values.includes(f.value)).flatMap(
    (f) => f.stages,
  );

const memberTaskSorter = (a: Task, b: Task) => {
  const aIsNeedsReview = a.stage === StageValue.WaitingOnClient;
  const bIsNeedsReview = b.stage === StageValue.WaitingOnClient;
  if (aIsNeedsReview && !bIsNeedsReview) return -1;
  if (!aIsNeedsReview && bIsNeedsReview) return 1;
  return defaultTaskSorter(a, b);
};

const TaskSectionHeader = ({
  title,
  icon,
  count,
  rightAction,
}: {
  title: string;
  icon: ReactElement;
  count: number;
  rightAction?: ReactElement;
}) => {
  const isMobile = useIsMobile();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: "8px",
        marginBottom: "24px",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        {icon}
        <Typography variant="h3">{title}</Typography>
        <Typography
          variant="bodyHeavy"
          sx={{ color: "#6B6E7B", marginTop: "4px" }}
        >
          {count}
        </Typography>
      </Box>
      {rightAction}
    </Box>
  );
};

const WishlistTaskItem = ({
  title,
  onAddToTasks,
  onDelete,
}: {
  title: string;
  onAddToTasks: () => void;
  onDelete: () => void;
}) => {
  const isMobile = useIsMobile();

  return (
    <FlexBox
      direction={isMobile ? "column" : "row"}
      variant="contained"
      justifyContent="space-between"
      alignItems="center"
      gap="8px"
      sx={{
        marginBottom: "8px",
        paddingRight: isMobile ? "" : "16px",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body" color="#3D3D3D">
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "12px",
          alignItems: "center",
          justifyContent: isMobile ? "space-between" : undefined,
        }}
      >
        <Button
          size="small"
          variant="text"
          onClick={onAddToTasks}
          sx={{
            height: "32px",
          }}
        >
          Add to tasks
        </Button>
        <IconButton size="small" onClick={onDelete} sx={{ color: "#8E9598" }}>
          {isMobile ? <Trash size={20} /> : <X size={24} />}
        </IconButton>
      </Box>
    </FlexBox>
  );
};

export default () => {
  useTitle("Tasks");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { pushSnack } = useSnackpack();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [wishlistEntries, setWishlistEntries] = useState<any[]>([]);
  const [showDoneTasks, setShowDoneTasks] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [projectOptions, setProjectOptions] = useState<
    { label: string; value: string; mutuallyExclusive: boolean }[]
  >([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const { data: memberDetails, isDigitalSubscription } = useMemberDetails();
  const { request: listTaskProjectsRequest } = useListTaskProjects();
  const { request: listWishlistEntriesRequest } =
    useListMemberTaskWishlistEntries();
  const { request: updateWishlistEntryRequest } =
    useUpdateMemberTaskWishlistEntry();

  const onAddTaskClick = () => {
    navigate("/tasks/add", { state: { family: memberDetails?.family } });
  };

  const { request, data } = useListTasksForMember((r) => {
    setTasks(r.tasks);
  });

  const fetchTasks = async () => {
    const resp = await request({
      projects: selectedProjects,
    });
    if (resp) {
      setTasks(resp.tasks);
    }
  };

  const fetchWishlistEntries = async () => {
    const resp = await listWishlistEntriesRequest({
      states: ["active"], // Only show active wishlist entries
    });
    if (resp) {
      setWishlistEntries(resp.taskWishlistEntries);
    }
  };

  const handleWishlistAddToTasks = (entry: TaskWishlistEntry) => {
    navigate("/tasks/add", {
      state: { taskWishlistEntry: entry, family: memberDetails?.family },
    });
  };

  const handleWishlistDelete = async (entryRef: string, title: string) => {
    await updateWishlistEntryRequest({
      ref: entryRef,
      taskRef: "",
      state: "deleted",
    });
    // Remove from local state immediately
    setWishlistEntries((prev) =>
      prev.filter((entry) => entry.ref !== entryRef),
    );
    pushSnack(`"${title}" removed from wishlist`);
  };

  const loadProjects = async () => {
    if (memberDetails?.family?.ref && !projectsLoaded) {
      const response = await listTaskProjectsRequest(
        new ListTaskProjectsRequest({
          familyRef: memberDetails.family.ref,
        }),
      );
      if (response) {
        const allProjects = Array.from(new Set([...response.projects]));
        const options = allProjects.map((project) => ({
          label: project,
          value: project,
          mutuallyExclusive: false,
        }));
        setProjectOptions(options);
      }
      setProjectsLoaded(true);
    }
  };

  const handleProjectChange = (
    selected: { label: string; value: string }[],
  ) => {
    const values = selected.map((s) => s.value).filter((v) => v !== "");
    setSelectedProjects(values);
  };

  const handleStageChange = (selected: { label: string; value: string }[]) => {
    setSelectedStages(selected.map((s) => s.value));
  };

  useEffect(() => {
    fetchTasks();
    fetchWishlistEntries();
  }, []);

  useEffect(() => {
    // Scroll to wishlist section if hash is present after data loads
    if (window.location.hash === "#wishlist" && data) {
      setTimeout(() => {
        const wishlistElement = document.getElementById("wishlist");
        if (wishlistElement) {
          wishlistElement.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [data, wishlistEntries]);

  useEffect(() => {
    if (memberDetails) {
      loadProjects();
    }
  }, [memberDetails, projectsLoaded]);

  useEffect(() => {
    fetchTasks();
  }, [selectedProjects]);

  let resultingTasks = tasks || [];
  if (query) {
    const queryPaths = ["title", "priority", "state", "family.name"];
    const regexPattern = new RegExp(
      query
        .split(" ")
        .map((sub) => `(${sub})`)
        .join("|"),
      "i",
    );

    resultingTasks = resultingTasks.filter((t) => {
      const searchableValues = queryPaths.map((path) => get(t, path)).join(" ");
      return regexPattern.test(searchableValues);
    });
  }

  const openTasks = useMemo(() => {
    let tasks = resultingTasks.filter(
      (task) => task.stage !== "done" && task.stage !== "deleted",
    );
    if (selectedStages.length > 0) {
      const allowedStages = getStagesForFilterValues(selectedStages);
      tasks = tasks.filter((task) => allowedStages.includes(task.stage));
    }
    return tasks;
  }, [resultingTasks, selectedStages]);
  const doneTasks = useMemo(
    () =>
      resultingTasks
        .filter((task) => task.stage === "done")
        .sort((a, b) => Number(b.lastUpdatedSec) - Number(a.lastUpdatedSec)),

    [resultingTasks],
  );

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Breadcrumbs
        breadcrumbs={[{ name: "Home", link: "/" }, { name: "Tasks" }]}
      />
      <Typography variant="display" sx={{ marginBottom: "24px" }}>
        Family tasks
      </Typography>
      {!data ? (
        <Loading />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              justifyContent: "space-between",
            }}
          >
            <SearchBar
              fullWidth={isMobile}
              onQuery={(query) => {
                setQuery(query);
                setShowDoneTasks(true);
              }}
              onKeyPress={true}
              sx={{ marginLeft: "0px" }}
            />
            <Button sx={{ minWidth: "120px" }} onClick={onAddTaskClick}>
              Add task
            </Button>
          </Box>
          <Box sx={{ width: "100%" }}>
            <TaskSectionHeader
              title="Open tasks"
              icon={<LoadingIcon height="24px" width="24px" />}
              count={openTasks.length}
              rightAction={
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    alignSelf: isMobile ? "flex-end" : "center",
                  }}
                >
                  <CheckedDropDown
                    sx={{
                      "& .MuiSelect-select": {
                        paddingLeft: "0px !important",
                      },
                    }}
                    sxPaper={{
                      "li + li": {
                        marginTop: "10px",
                      },
                    }}
                    options={stageFilterOptions}
                    initial={selectedStages}
                    onChange={handleStageChange}
                    displayValue={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Status
                        {selectedStages.length > 0 && (
                          <EllipseIcon
                            height={6}
                            width={6}
                            style={{ marginLeft: "4px" }}
                          />
                        )}
                      </Box>
                    }
                    noOutline={true}
                  />
                  <CheckedDropDown
                    sx={{
                      "& .MuiSelect-select": {
                        paddingLeft: "0px !important",
                      },
                    }}
                    options={projectOptions}
                    initial={selectedProjects}
                    onChange={handleProjectChange}
                    disabled={projectsLoaded && projectOptions.length === 0}
                    displayValue={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {projectsLoaded && projectOptions.length === 0
                          ? "No projects"
                          : "Project"}
                        {selectedProjects && selectedProjects.length > 0 && (
                          <EllipseIcon
                            height={6}
                            width={6}
                            style={{ marginLeft: "4px" }}
                          />
                        )}
                      </Box>
                    }
                    noOutline={true}
                  />
                </Box>
              }
            />
            <MemberTaskSection
              tasks={openTasks.sort(memberTaskSorter)}
              compact={isMobile ? true : false}
            />
          </Box>
          <Box id="wishlist" sx={{ width: "100%" }}>
            <TaskSectionHeader
              title="Save for later"
              icon={<Heart size={24} color="#EF7B77" strokeWidth={2.5} />}
              count={wishlistEntries.length}
            />
            {wishlistEntries.length > 0 ? (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {wishlistEntries.map((entry) => (
                  <WishlistTaskItem
                    key={entry.ref}
                    title={entry.title}
                    onAddToTasks={() => handleWishlistAddToTasks(entry)}
                    onDelete={() =>
                      handleWishlistDelete(entry.ref, entry.title)
                    }
                  />
                ))}
              </Box>
            ) : (
              <FlexBox
                variant="contained"
                direction="column"
                alignItems="center"
                gap="12px"
                sx={{ padding: "32px 16px" }}
              >
                <Heart size={48} color="#8E9598" strokeWidth={1.5} />
                <Typography
                  variant="body"
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                >
                  Save task ideas for later by clicking the heart icon on
                  personalized suggestions
                </Typography>
              </FlexBox>
            )}
          </Box>
          <Box sx={{ width: "100%" }}>
            <TaskSectionHeader
              title="Done"
              icon={<CheckmarkIcon />}
              count={doneTasks.length}
            />
            {showDoneTasks || doneTasks.length === 0 ? (
              <MemberTaskSection
                tasks={doneTasks}
                compact={isMobile ? true : false}
              />
            ) : (
              <Button
                variant="outlined"
                borderColor="border"
                onClick={() => {
                  setShowDoneTasks(!showDoneTasks);
                }}
              >
                Show {doneTasks.length} finished tasks
              </Button>
            )}
          </Box>
          {!isDigitalSubscription && (
            <TaskInspiration
              family={memberDetails?.family!}
              activeTasks={memberDetails?.activeTasks || []}
            />
          )}
        </Box>
      )}
    </FlexPage>
  );
};
